import React, { cloneElement, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import modernStyles from './InputModern.module.sass'
import traditionalStyles from './InputTraditional.module.sass'
import classNames from 'classnames/bind'
import uniqueId from 'lodash/uniqueId'
import { mutuallyExclusiveProps } from 'airbnb-prop-types'
import FieldError from '../FieldError'
import { propWarning } from '../utils'
import Field from '../Field'

const Input = ({
  onChange,
  onFocus,
  onBlur,
  label,
  labelFloat,
  labelTooltip,
  altTheme,
  errorAlign,
  className,
  meta: { touched, error: metaError, warning } = {},
  readOnly,
  textarea,
  leftElements,
  rightElements,
  onEnterKeyDown,
  placeholder,
  fieldRef,
  defaultValue,
  value: valueProp,
  id,
  type = 'text',
  disabled = false,
  input = {},
  required = false,
  error = '',
  login = false,
  traditional = false,
  ...other
}) => {
  const { value: inputValueProp } = input
  const passedValue =
    inputValueProp !== undefined ? inputValueProp : valueProp || defaultValue
  const [isFocused, setIsFocused] = useState(false)
  const [hasValue, setHasValue] = useState(Boolean(passedValue))
  const [value, setValue] = useState(passedValue || '')
  const [inputId, setInputId] = useState(input.name || name || id)
  const handleChange = event => {
    if (typeof input.onChange === 'function') {
      input.onChange(event)
    }

    if (typeof onChange === 'function') {
      onChange(event)
    }

    const value = event.target.value
    setValue(value)
    setHasValue(Boolean(value))
  }
  const handleFocus = event => {
    if (typeof input.onFocus === 'function') {
      input.onFocus(event)
    }
    if (onFocus !== undefined) {
      onFocus(event)
    }
    setIsFocused(true)
  }
  const handleBlur = event => {
    if (typeof input.onBlur === 'function') {
      input.onBlur(event)
    }
    if (onBlur !== undefined) {
      onBlur(event)
    }
    setIsFocused(false)
  }
  const styles =
    textarea || traditional || login ? traditionalStyles : modernStyles
  const themeStyles = { ...styles, ...altTheme }
  const cx = classNames.bind(themeStyles)
  const hasError = Boolean((touched && metaError) || error)
  const hasWarning = Boolean(warning)
  const EnhancedLabelTooltip =
    labelTooltip &&
    cloneElement(labelTooltip, {
      className: cx(labelTooltip.props.className, themeStyles.tooltipButton),
      id: `${inputId}-tooltip`
    })
  const pattern = type === 'number' ? '[0-9]*' : null
  const inputRef = fieldRef || useRef(null)

  const leftElementsRef = useRef(null)

  const EnhancedLeftElements = (
    <div ref={leftElementsRef} className={themeStyles.sideElements}>
      {leftElements}
    </div>
  )
  useEffect(() => {
    // To set value/hasValue from a browser autofill
    let currentValue = value
    let autofillDelay
    if (inputRef.current) {
      // The change event is not fired in Chrome iOS and possibly other browsers on autofill
      // According to Chrome devs, this is for security reasons
      // The value is only available once the user interacts with the page
      // Here, we detect an autofill and set the hasValue state appropriately
      // https://github.com/facebook/react/issues/1159
      // https://bugs.chromium.org/p/chromium/issues/detail?id=352527
      autofillDelay = setTimeout(() => {
        let autofilled
        try {
          autofilled = inputRef.current.matches(':autofill')
          if (autofilled) {
            currentValue = inputRef.current.value
          }
        } catch (error) {
          try {
            autofilled = inputRef.current.matches(':-webkit-autofill')
            if (autofilled) {
              currentValue = inputRef.current.value
            }
          } catch (error) {}
        }
        setValue(currentValue) // This may not be worth leaving - autofill values are not available for security reasons
        setHasValue(Boolean(currentValue || autofilled))
      }, 500)
    }
    if (input.onChange !== undefined) {
      input.onChange(currentValue)
    }
    return () => clearTimeout(autofillDelay)
  }, [])
  useEffect(() => {
    if (!inputId) {
      setInputId(`${textarea ? 'textarea' : 'input'}-${uniqueId()}`)
    }
  }, [])
  useEffect(() => {
    setValue(passedValue)
  }, [passedValue])
  return (
    <div className={className}>
      <div
        className={cx(themeStyles.container, {
          disabled,
          login,
          isFocused,
          hasError,
          readOnly,
          hasSideElements: rightElements || leftElements
        })}
      >
        {label && !login ? (
          <label
            htmlFor={inputId}
            style={
              !traditional
                ? {
                    marginLeft:
                      leftElementsRef.current &&
                      !hasValue &&
                      !isFocused &&
                      leftElementsRef.current.clientWidth
                  }
                : {}
            }
            className={cx(themeStyles.label, {
              labelFocused: isFocused,
              hasValue,
              labelDisabled: disabled,
              labelReadOnly: readOnly,
              labelFloat
            })}
          >
            {label}
            {required && <span className={themeStyles.required}> *</span>}
            {EnhancedLabelTooltip}
          </label>
        ) : null}
        <Field
          {...other}
          textarea={textarea}
          hasError={hasError}
          isFocused={isFocused}
          readOnly={readOnly}
          disabled={disabled}
          id={inputId}
          type={type}
          altTheme={altTheme}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          onEnterKeyDown={onEnterKeyDown}
          value={value}
          traditional={traditional}
          placeholder={login ? placeholder || label : placeholder}
          fieldRef={inputRef}
          required={required}
          pattern={pattern}
          leftElements={leftElements && EnhancedLeftElements}
          rightElements={rightElements}
        />
      </div>
      {hasError && (
        <FieldError className={themeStyles.errorMessage} align={errorAlign}>
          {metaError || error}
        </FieldError>
      )}
      {hasWarning && !hasError && (
        <FieldError style={{ color: '#000' }}>{warning}</FieldError>
      )}
    </div>
  )
}

Input.displayName = 'Input'
Input.propTypes = {
  /**
   * @deprecated in version 2.7
   *
   * An optional button element to be contained within the component
   * This is deprecated in favor of `rightElements` prop which can contain multiple elements
   */
  button: propWarning(
    'Deprecated prop: This prop will be removed in version 3. Use `rightElements` prop instead.'
  ),
  /**
   * An optional node of elements to be contained within the Input component
   */
  leftElements: PropTypes.node,
  /**
   * An optional node of elements to be contained within the Input component
   */
  rightElements: PropTypes.node,
  /**
   * The input ID
   */
  id: PropTypes.string,
  /**
   * The name used for the label element "for" and input "name" attributes (if not using redux form)
   */
  name: PropTypes.string,
  /**
   * A custom onChange event
   */
  onChange: PropTypes.func,
  /**
   * The type of input (not used for textarea)
   */
  type: PropTypes.oneOf([
    'text',
    'number',
    'password',
    'tel',
    'url',
    'email',
    'search'
  ]),
  /**
   * The placeholder text shown inside the input before text is entered (not used with "labelFloat")
   */
  placeholder: mutuallyExclusiveProps(
    PropTypes.string,
    'placeholder',
    'labelFloat'
  ),
  /**
   * The error shown below the input (not used if there is a redux-form style 'meta.error' value)
   */
  error: PropTypes.node,
  /**
   * the alignment of the error
   */
  errorAlign: PropTypes.string,
  /**
   * Whether or not the input is disabled
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, this is a login style input
   */
  login: PropTypes.bool,
  /**
   * The connected input props (if using redux-form)
   */
  input: PropTypes.object,
  /**
   * Metadata about the state of this field (if using redux-form)
   */
  meta: PropTypes.object,
  /**
   * Called when enter key is pressed
   */
  onEnterKeyDown: PropTypes.func,
  /**
   * If `true`, this input requires a value to submit the form (marks an existing label with an asterisk)
   */
  required: PropTypes.bool,
  /**
   * The label text (if any)
   */
  label: PropTypes.string,
  /**
   * @ignore
   */
  labelFloat: mutuallyExclusiveProps(
    PropTypes.bool,
    'placeholder',
    'labelFloat'
  ),
  /**
   * If `true`. the input has a password visibility toggle button (only available for type="password")
   */
  hasPasswordToggle: PropTypes.bool,
  /**
   * A Tooltip component used for the label
   */
  labelTooltip: PropTypes.node,
  /**
   * An additional custom className
   */
  className: PropTypes.string,
  /**
   * A CSS modules style object to override default theme
   */
  altTheme: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  /**
   * If `true`, the input uses the traditional style
   */
  traditional: PropTypes.bool,
  /**
   * Default value to initialize the input, will only be taken into consideration on first render
   */
  defaultValue: PropTypes.string,
  /**
   * Current value of the input. Only when used as controlled input
   */
  value: PropTypes.string,
  /**
   * If `true`, the input is read-only
   */
  readOnly: PropTypes.bool,
  /**
   * Called when input is focused
   */
  onFocus: PropTypes.func,
  /**
   * Called when input is blurred
   */
  onBlur: PropTypes.func,
  /**
   * If `true`, the field will render as a textarea
   */
  textarea: PropTypes.bool,
  /**
   * If defined, a ref for the field
   */
  fieldRef: PropTypes.object,
  /**
   * @ignore
   */
  autosize: PropTypes.bool,
  /**
   * @ignore
   */
  resize: PropTypes.oneOf([
    '',
    'both',
    'horizontal',
    'vertical',
    'block',
    'inline'
  ]),
  /**
   * @ignore
   */
  minHeight: PropTypes.number,
  /**
   * @ignore
   */
  maxHeight: PropTypes.number
}

export default Input
